import React, { useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Colors, Radius, Shadow, Typography } from '../theme';
import type { useAuth } from '../hooks/useAuth';
import { useI18n } from '../i18n';

interface Props {
  onCompletePartnerPasswordSetup: ReturnType<typeof useAuth>['completePartnerPasswordSetup'];
  onSignOut: () => Promise<void>;
}

export default function PartnerSetPasswordScreen({ onCompletePartnerPasswordSetup, onSignOut }: Props) {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const submit = async () => {
    setMessage('');
    if (password.length < 6) {
      setMessage(t('resetPassword.weakPassword'));
      return;
    }
    if (password !== confirmPassword) {
      setMessage(t('resetPassword.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await onCompletePartnerPasswordSetup(password);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('partner.setPasswordFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LinearGradient colors={['#0B1826', '#070B14', '#020409']} style={styles.container}>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          contentContainerStyle={[styles.scroll, { paddingTop: insets.top + 36, paddingBottom: insets.bottom + 28 }]}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.card}>
            <Text style={styles.kicker}>{t('partner.kicker')}</Text>
            <Text style={styles.title}>{t('partner.createPassword')}</Text>
            <Text style={styles.subtitle}>{t('partner.firstSignIn')}</Text>

            <View style={styles.form}>
              <Text style={styles.label}>{t('resetPassword.newPassword')}</Text>
              <View style={styles.passwordField}>
                <Icon name="lock" size={18} color="#FF7A00" />
                <TextInput
                  value={password}
                  onChangeText={setPassword}
                  placeholder={t('resetPassword.minimumCharacters')}
                  placeholderTextColor={Colors.textMuted}
                  secureTextEntry={!showPassword}
                  textContentType="newPassword"
                  autoCapitalize="none"
                  style={styles.input}
                />
                <TouchableOpacity onPress={() => setShowPassword(current => !current)} hitSlop={10}>
                  <Icon name={showPassword ? 'eye-off' : 'eye'} size={19} color={Colors.textMuted} />
                </TouchableOpacity>
              </View>

              <Text style={styles.label}>{t('resetPassword.confirmPassword')}</Text>
              <TextInput
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                placeholder={t('resetPassword.enterAgain')}
                placeholderTextColor={Colors.textMuted}
                secureTextEntry={!showPassword}
                textContentType="newPassword"
                autoCapitalize="none"
                style={[styles.passwordField, styles.confirmInput]}
              />

              {message ? <Text style={styles.formError}>{message}</Text> : null}

              <TouchableOpacity style={styles.primaryButton} onPress={submit} activeOpacity={0.84} disabled={submitting}>
                {submitting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.primaryButtonText}>{t('partner.setPasswordContinue')}</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity onPress={() => void onSignOut()} disabled={submitting} style={styles.signOutButton}>
                <Text style={styles.signOutText}>{t('partner.signOut')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  card: {
    width: '100%',
    maxWidth: 430,
    alignSelf: 'center',
    padding: 24,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(9,17,29,0.92)',
    ...Shadow.strong,
  },
  kicker: {
    color: '#FF7A00',
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
    letterSpacing: 3,
    textAlign: 'center',
  },
  title: {
    marginTop: 10,
    color: Colors.textPrimary,
    fontSize: Typography.xxl,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  subtitle: {
    marginTop: 8,
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    lineHeight: 21,
    textAlign: 'center',
  },
  form: {
    marginTop: 28,
  },
  label: {
    marginBottom: 8,
    color: 'rgba(255,255,255,0.8)',
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  passwordField: {
    minHeight: 52,
    marginBottom: 18,
    paddingHorizontal: 15,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(0,0,0,0.2)',
    flexDirection: 'row',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    paddingHorizontal: 12,
    color: Colors.textPrimary,
    fontSize: Typography.base,
  },
  confirmInput: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    paddingVertical: 13,
  },
  formError: {
    marginBottom: 16,
    padding: 12,
    borderRadius: Radius.lg,
    color: '#FECDD3',
    backgroundColor: 'rgba(244,63,94,0.09)',
    fontSize: Typography.sm,
    lineHeight: 20,
  },
  primaryButton: {
    width: '100%',
    minHeight: 52,
    marginTop: 6,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF7A00',
    ...Shadow.glow,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.extrabold,
  },
  signOutButton: {
    marginTop: 18,
    alignItems: 'center',
  },
  signOutText: {
    color: Colors.textMuted,
    fontSize: Typography.sm,
    textDecorationLine: 'underline',
  },
});
